/**
 * LAYOUT MATH (B10) — pure functions behind the shells, tested in layoutMath.test.ts.
 * No DOM, no React: safe to import from anywhere (camera code, tests, scripts).
 *
 *   detectVariant(vw, kiosk)       §A.1 breakpoints → mobile / compact / desktop / large / kiosk
 *   METRICS[variant]               §A.1–A.5 numbers (G, Wp, Wc, M, Z, k, …)
 *   computeInsets(input)           §A.6 safe insets published to the camera
 *   panelYields(variant, vw)       narrow desktop: the side panel steps aside while a card is open
 *   sheetHeights(vh, mode, safe)   §A.4 bottom-sheet snaps
 *   resolveSnap(h, v, heights)     §A.4 release rule (0.45 px/ms → one snap in the drag direction)
 *   releaseVelocity(samples, now)  px/ms of the last 100 ms of a drag
 *   rubberBand(h, heights)         never below peek; soft resistance above full
 *   decideDragIntent(…)            sheet drag vs content scroll vs chips' sideways scroll
 *   sheetCover(h, heights)         0 at half → 1 at full (riding chrome fades)
 */
import type { Insets, Layout, SheetSnap } from "@/contracts/store";

export type LayoutVariant = "mobile" | "compact" | "desktop" | "large" | "kiosk";
export type SheetMode = "panel" | "card";

export interface LayoutMetrics {
  variant: LayoutVariant;
  /** The store's coarse layout (compact and large are both "desktop"). */
  layout: Layout;
  /** Type factor --ld-k (kiosk 1.2). */
  k: number;
  /** Gutter G (mobile: the chrome gutter; the sheet itself is edge to edge). */
  gutter: number;
  /** Panel width Wp (0 on mobile: the panel lives in the sheet). */
  panelW: number;
  /** Card width Wc (0 on mobile). */
  cardW: number;
  /** Route meter height M. */
  meterH: number;
  /** Zoom island column width Z (mobile: the 44 px solo buttons). */
  zoomW: number;
  /** Distance from the viewport bottom to the LightIsland / attribution row. */
  chromeBottom: number;
  /** LightIsland height (76; kiosk 96). */
  lightH: number;
  /** Minimum touch/click target. */
  minTarget: number;
}

/** Breathing space B between chrome and the camera frame (§A.6). */
export const BREATHING = 32;
/** Gap between the zoom island and the card (§A.6: G + Z + 12 + Wc + B). */
export const CARD_GAP = 12;
/** Mobile ≤ 720, compact 721–1099, desktop 1100–1679, large ≥ 1680 (§A.1). */
export const BREAKPOINTS = { mobileMax: 720, compactMax: 1099, desktopMax: 1679 } as const;

export const METRICS: Record<LayoutVariant, LayoutMetrics> = {
  mobile: { variant: "mobile", layout: "mobile", k: 1, gutter: 12, panelW: 0, cardW: 0, meterH: 40, zoomW: 44, chromeBottom: 12, lightH: 42, minTarget: 44 },
  // compact: chromeBottom is not in the spec table; 12 keeps the island off the edge at G = 16 (desktop uses G − 8).
  compact: { variant: "compact", layout: "desktop", k: 1, gutter: 16, panelW: 344, cardW: 360, meterH: 44, zoomW: 48, chromeBottom: 12, lightH: 76, minTarget: 40 },
  desktop: { variant: "desktop", layout: "desktop", k: 1, gutter: 24, panelW: 376, cardW: 392, meterH: 44, zoomW: 48, chromeBottom: 16, lightH: 76, minTarget: 40 },
  large: { variant: "large", layout: "desktop", k: 1, gutter: 32, panelW: 420, cardW: 420, meterH: 44, zoomW: 48, chromeBottom: 24, lightH: 76, minTarget: 40 },
  kiosk: { variant: "kiosk", layout: "kiosk", k: 1.2, gutter: 32, panelW: 480, cardW: 460, meterH: 56, zoomW: 64, chromeBottom: 32, lightH: 96, minTarget: 56 },
};

/** Mobile insets (§A.6, 390×844 column): top = meter top 68 + 16, right = 16 + 44 + 8, bottom = sheet + 16. */
export const MOBILE_INSETS = { top: 84, left: 16, right: 68, belowSheet: 16 } as const;

/** Sheet geometry (§A.4). Heights are above the bottom edge, not counting the safe-area inset. */
export const SHEET = {
  peek: 130,
  halfRatio: 0.5,
  halfMin: 360,
  halfMax: 480,
  cardHalfMax: 460,
  cardHalfRatio: 0.6,
  fullGap: 72,
  /** A release faster than this (px/ms) moves one snap in the drag direction. */
  flick: 0.45,
  /** Velocity window (ms). */
  velocityWindow: 100,
  /** Drag starts after this much vertical travel (px), when vertical dominates. */
  slop: 6,
  /** Soft travel allowed above full (px). */
  overscroll: 24,
} as const;

export const SNAPS: readonly SheetSnap[] = ["peek", "half", "full"];

const clampN = (v: number, a: number, b: number): number => (v < a ? a : v > b ? b : v);

/** §A.1: layout variant for a viewport width. `?kiosk=1` always wins (kiosk is 1920×1080 CSS). */
export function detectVariant(vw: number, kiosk: boolean): LayoutVariant {
  if (kiosk) return "kiosk";
  if (vw <= BREAKPOINTS.mobileMax) return "mobile";
  if (vw <= BREAKPOINTS.compactMax) return "compact";
  if (vw <= BREAKPOINTS.desktopMax) return "desktop";
  return "large";
}

/** `?kiosk=1` (also "true"/"yes"). Read-only: the app never writes it. */
export function kioskFromSearch(search: string): boolean {
  const v = new URLSearchParams(search).get("kiosk");
  return v === "1" || v === "true" || v === "yes";
}

/**
 * §A.4 snap heights for a viewport height (above the bottom safe-area inset). Card mode: half = min(460, 60 vh).
 * Full leaves 72 px of map above the sheet below the top safe-area inset (§A.4 "add env(safe-area-inset-*)
 * to every edge value"): its top edge sits at 72 + inset-top, never under a notch or the status bar.
 */
export function sheetHeights(vh: number, mode: SheetMode = "panel", safe: Pick<Insets, "top" | "bottom"> = { top: 0, bottom: 0 }): Record<SheetSnap, number> {
  const peek = SHEET.peek;
  const full = Math.max(peek, Math.round(vh - SHEET.fullGap - safe.top - safe.bottom));
  const rawHalf = mode === "card" ? Math.min(SHEET.cardHalfMax, SHEET.cardHalfRatio * vh) : clampN(SHEET.halfRatio * vh, SHEET.halfMin, SHEET.halfMax);
  const half = Math.round(clampN(rawHalf, peek, full));
  return { peek, half, full };
}

export interface InsetInput {
  variant: LayoutVariant;
  vw: number;
  vh: number;
  /** Desktop/kiosk: a place is selected (the card is open or opening). */
  cardOpen: boolean;
  /** The side panel has stepped aside (panelYields). Default: whenever the card is open on a narrow desktop. */
  panelHidden?: boolean;
  /** Mobile: the sheet's current snap and content. */
  sheet?: SheetSnap;
  sheetMode?: SheetMode;
  /** env(safe-area-inset-*) in px. */
  safe?: Insets;
}

const ZERO: Insets = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * §0 "select = the land answers": the camera frame between the panel and an open card must stay wide
 * enough to show the route, the site and the pin. Below this width the panel steps aside while a card is
 * open (the card's prev/next keep the list order), instead of the camera framing the land under it.
 * Desktop 1440 has 500 and 1366 has 426; the panel yields below 1340 (desktop) and on every compact
 * width. Measured over all 103 routes (select framing audit): at 400 px every route, site and pin fit;
 * at 360 (1300 wide) the Purandar route already runs 5 % under the card.
 */
export const MIN_CARD_FRAME = 400;

/** Width of the card-open camera frame with the panel on screen (§A.6 formulas). */
export function cardFrameWidth(variant: LayoutVariant, vw: number): number {
  const m = METRICS[variant];
  return vw - (m.gutter + m.panelW + BREATHING) - (m.gutter + m.zoomW + CARD_GAP + m.cardW + BREATHING);
}

/** Whether the side panel steps aside while a card is open (never on mobile, where the sheet holds both, or the kiosk). */
export function panelYields(variant: LayoutVariant, vw: number): boolean {
  if (variant === "mobile" || variant === "kiosk") return false;
  return cardFrameWidth(variant, vw) < MIN_CARD_FRAME;
}

/**
 * §A.6 safe insets for the camera.
 *  top    G + M + 20                       (mobile 84)
 *  left   G + Wp + B                       (mobile 16)
 *  right  G + Z + B, card: G + Z + 12 + Wc + B   (mobile 68)
 *  bottom LightIsland bottom + height + 4  (mobile: sheet height + 16)
 * Every edge adds its env(safe-area-inset-*). On mobile the full snap frames as
 * half: the map is covered at full, and collapsing back needs no re-framing.
 * On a narrow desktop with the card open the panel steps aside (panelYields):
 * left = G + B, so the frame is never under the panel. The frame never
 * collapses: when the edges leave less than max(160 px, 15 %), the left inset
 * gives way first, then both sides shrink in proportion.
 */
export function computeInsets(input: InsetInput): Insets {
  const { variant, vw, vh, cardOpen } = input;
  const safe = input.safe ?? ZERO;
  const m = METRICS[variant];
  let out: Insets;
  if (variant === "mobile") {
    const heights = sheetHeights(vh, input.sheetMode ?? "panel", safe);
    const snap = input.sheet ?? "peek";
    const h = snap === "full" ? heights.half : heights[snap];
    out = {
      top: MOBILE_INSETS.top + safe.top,
      left: MOBILE_INSETS.left + safe.left,
      right: MOBILE_INSETS.right + safe.right,
      bottom: h + safe.bottom + MOBILE_INSETS.belowSheet,
    };
  } else {
    const G = m.gutter;
    const panelHidden = input.panelHidden ?? (cardOpen && panelYields(variant, vw));
    out = {
      top: G + m.meterH + 20 + safe.top,
      left: (panelHidden ? G : G + m.panelW) + BREATHING + safe.left,
      right: (cardOpen ? G + m.zoomW + CARD_GAP + m.cardW + BREATHING : G + m.zoomW + BREATHING) + safe.right,
      bottom: m.chromeBottom + m.lightH + 4 + safe.bottom,
    };
  }
  return fitFrame(out, vw, vh, variant === "mobile" ? MOBILE_INSETS.left + safe.left : m.gutter + BREATHING + safe.left);
}

/** Keeps a usable frame: at least max(160 px, 15 %) of each viewport dimension. */
export function fitFrame(i: Insets, vw: number, vh: number, minLeft = 0): Insets {
  const out = { ...i };
  const minW = Math.max(160, Math.round(vw * 0.15));
  const minH = Math.max(160, Math.round(vh * 0.15));
  if (vw > 0 && out.left + out.right > vw - minW) {
    // the left edge gives way first, down to its own gutter
    out.left = Math.max(Math.min(out.left, minLeft), vw - minW - out.right);
    if (out.left + out.right > vw - minW) {
      const room = Math.max(0, vw - minW);
      const s = room / (out.left + out.right || 1);
      out.left = Math.floor(out.left * s);
      out.right = Math.floor(out.right * s);
    }
  }
  if (vh > 0 && out.top + out.bottom > vh - minH) {
    const room = Math.max(0, vh - minH);
    const s = room / (out.top + out.bottom || 1);
    out.top = Math.floor(out.top * s);
    out.bottom = Math.floor(out.bottom * s);
  }
  return out;
}

/* ── sheet physics ──────────────────────────────────────────────────── */

export interface DragSample {
  t: number; // ms (performance.now / event.timeStamp)
  h: number; // sheet height at t
}

/**
 * dh/dt in px/ms at release (positive = growing / dragged up).
 *  - 0 when the pointer rested: no sample in the last `window` ms before `now`.
 *  - Otherwise the slope over the samples within `window` ms before the LAST
 *    sample, and never fewer than two: on a janky device moves arrive one per
 *    (slow) frame, and a real flick must still read as one.
 */
export function releaseVelocity(samples: readonly DragSample[], now: number, window: number = SHEET.velocityWindow): number {
  if (samples.length < 2) return 0;
  const b = samples[samples.length - 1];
  if (now - b.t > window) return 0;
  let i = samples.length - 2;
  while (i > 0 && b.t - samples[i - 1].t <= window) i--;
  const a = samples[i];
  const dt = b.t - a.t;
  if (dt <= 0) return 0;
  return (b.h - a.h) / dt;
}

/**
 * §A.4 release rule: above 0.45 px/ms the sheet moves one snap in the direction
 * of the release (from where it is now); otherwise it goes to the nearest snap.
 * Never below peek.
 */
export function resolveSnap(h: number, velocity: number, heights: Record<SheetSnap, number>): SheetSnap {
  // A snap within 12 px counts as "where the sheet is": a flick from just under half goes on to full.
  const eps = 12;
  if (Math.abs(velocity) > SHEET.flick) {
    if (velocity > 0) return SNAPS.find((s) => heights[s] > h + eps) ?? "full";
    const below = SNAPS.filter((s) => heights[s] < h - eps);
    return below.length ? below[below.length - 1] : "peek";
  }
  let best: SheetSnap = "peek";
  let bestD = Number.POSITIVE_INFINITY;
  for (const s of SNAPS) {
    const d = Math.abs(heights[s] - h);
    if (d < bestD) {
      best = s;
      bestD = d;
    }
  }
  return best;
}

/** Drag position → sheet height: hard floor at peek, soft resistance above full (≤ 24 px). */
export function rubberBand(h: number, heights: Record<SheetSnap, number>): number {
  if (h <= heights.peek) return heights.peek;
  if (h <= heights.full) return h;
  const over = h - heights.full;
  return heights.full + SHEET.overscroll * (1 - Math.exp(-over / (SHEET.overscroll * 2.5)));
}

/** One snap up (cycling to half from full) — the grip button's keyboard/click action. */
export function nextSnap(s: SheetSnap, dir: 1 | -1): SheetSnap {
  const i = SNAPS.indexOf(s);
  return SNAPS[clampN(i + dir, 0, SNAPS.length - 1)];
}

export type DragIntent = "pending" | "drag" | "native";

/**
 * Whether a pointer that went down on the sheet moves the sheet or scrolls its
 * content. dy > 0 = the finger moved down (screen coordinates).
 *  - within the slop: undecided;
 *  - mostly horizontal: native (the chips row scrolls sideways);
 *  - on the handle (grip zone): the sheet;
 *  - below full, anywhere: the sheet (the list does not scroll until the sheet is full);
 *  - at full: the content scrolls, unless it is at its top and the finger moves down.
 * `scrollTop` is the nearest scrolling container's scrollTop, or null when there is none.
 */
export function decideDragIntent(o: {
  dx: number;
  dy: number;
  zone: "handle" | "content";
  snap: SheetSnap;
  scrollTop: number | null;
  slop?: number;
}): DragIntent {
  const slop = o.slop ?? SHEET.slop;
  const ax = Math.abs(o.dx);
  const ay = Math.abs(o.dy);
  if (ax < slop && ay < slop) return "pending";
  if (ax >= ay) return "native";
  if (o.zone === "handle" || o.snap !== "full" || o.scrollTop === null) return "drag";
  return o.dy > 0 && o.scrollTop <= 0 ? "drag" : "native";
}

/** 0 at half or below, 1 at full: chrome riding on the sheet fades as it covers the map. */
export function sheetCover(h: number, heights: Record<SheetSnap, number>): number {
  const span = heights.full - heights.half;
  if (span <= 0) return h >= heights.full ? 1 : 0;
  const c = (h - heights.half) / span;
  return c <= 0 ? 0 : c >= 1 ? 1 : Math.round(c * 1000) / 1000;
}

/** Grip button action: up a snap; from full, back to half (Enter / Space / click). */
export function gripSnap(s: SheetSnap): SheetSnap {
  return s === "full" ? "half" : nextSnap(s, 1);
}

/** Sheet motion (§E.9): 640 settle up, 448 leave down; reduced motion 160. */
export function sheetMotion(from: number, to: number, reduced: boolean): { ms: number; ease: "settle" | "leave" } {
  if (reduced) return { ms: 160, ease: "settle" };
  return to >= from ? { ms: 640, ease: "settle" } : { ms: 448, ease: "leave" };
}
